// Feature flag utilities for gradual rollout of new features

/**
 * Check if a feature flag is enabled
 * @param {string} flagName - Name of the feature flag
 * @param {Object} options - Options for feature flag evaluation
 * @returns {boolean} - Whether the feature is enabled
 */
export const useFeatureFlag = (flagName, options = {}) => {
  const {
    defaultValue = false,
    userId = null,
    rolloutPercentage = 0
  } = options;

  // Check localStorage for override (useful for development/testing)
  const localStorageKey = `feature_flag_${flagName}`;
  const localOverride = localStorage.getItem(localStorageKey);
  
  if (localOverride !== null) {
    return localOverride === 'true';
  }

  // Check environment variables for feature flags
  const envKey = `REACT_APP_FEATURE_${flagName.toUpperCase()}`;
  const envValue = process.env[envKey];
  
  if (envValue !== undefined) {
    return envValue === 'true';
  }

  // Gradual rollout based on user ID hash
  if (userId && rolloutPercentage > 0) {
    const hash = simpleHash(userId);
    const userPercentage = hash % 100;
    return userPercentage < rolloutPercentage;
  }

  return defaultValue;
};

/**
 * Simple hash function for consistent user-based rollouts
 * @param {string} str - String to hash
 * @returns {number} - Hash value
 */
const simpleHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

/**
 * Feature flag configurations
 */
export const FEATURE_FLAGS = {
  RICH_MENTION_EDITOR: 'rich_mention_editor',
  ENHANCED_MOBILE_MENTIONS: 'enhanced_mobile_mentions',
  MENTION_TOOLTIPS: 'mention_tooltips'
};

/**
 * Hook to check if rich mention editor should be used
 * @param {string} userId - Current user ID for rollout
 * @returns {boolean} - Whether to use rich editor
 */
export const useRichMentionEditor = (userId = null) => {
  return useFeatureFlag(FEATURE_FLAGS.RICH_MENTION_EDITOR, {
    defaultValue: true, // Default to enabled for new feature
    userId,
    rolloutPercentage: 100 // 100% rollout initially, can be reduced if issues arise
  });
};

/**
 * Development utilities for testing feature flags
 */
export const featureFlagUtils = {
  /**
   * Enable a feature flag in localStorage
   * @param {string} flagName - Feature flag name
   */
  enable: (flagName) => {
    localStorage.setItem(`feature_flag_${flagName}`, 'true');
  },

  /**
   * Disable a feature flag in localStorage
   * @param {string} flagName - Feature flag name
   */
  disable: (flagName) => {
    localStorage.setItem(`feature_flag_${flagName}`, 'false');
  },

  /**
   * Clear a feature flag override from localStorage
   * @param {string} flagName - Feature flag name
   */
  clear: (flagName) => {
    localStorage.removeItem(`feature_flag_${flagName}`);
  },

  /**
   * Get all active feature flag overrides
   * @returns {Object} - Object with flag names and values
   */
  getOverrides: () => {
    const overrides = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('feature_flag_')) {
        const flagName = key.replace('feature_flag_', '');
        overrides[flagName] = localStorage.getItem(key) === 'true';
      }
    }
    return overrides;
  }
};

// Expose feature flag utils to window for debugging in development
if (process.env.NODE_ENV === 'development') {
  window.featureFlags = featureFlagUtils;
}